import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const PROMO_SOURCE = 'cart-bogo-same-product';
const GIFT_PROPERTY = '_lv_bogo_same_product_gift';
const SOURCE_VARIANT_PROPERTY = '_lv_bogo_same_product_source_variant_id';

const getGiftLock = () => window.__giftAddingLock || false;
const setGiftLock = (value) => {
  window.__giftAddingLock = value;
};

/**
 * @param {string | undefined} value
 * @returns {Set<number>}
 */
function parseVariantIds(value) {
  return new Set(
    (value || '')
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter(Number.isFinite)
  );
}

/**
 * @param {any} item
 * @returns {boolean}
 */
function isAutoGiftItem(item) {
  const properties = item.properties || {};

  return (
    properties[GIFT_PROPERTY] === 'true' ||
    properties._bogo_quantity_gift === 'true' ||
    properties._bogo_collection_gift === 'true' ||
    properties._auto_added_threshold === 'true' ||
    properties._freegifincart === 'true' ||
    properties._free_gift === 'true'
  );
}

/**
 * @param {Map<number, any[]>} map
 * @param {number} key
 * @param {any} value
 */
function pushToMapArray(map, key, value) {
  if (!map.has(key)) map.set(key, []);
  map.get(key).push(value);
}

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {number} [attempts]
 * @returns {Promise<Response>}
 */
async function fetchWithRetry(url, options, attempts = 3) {
  /** @type {unknown} */
  let lastError;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await fetch(url, options);
    } catch (error) {
      lastError = error;
      if (attempt < attempts) await wait(350 * attempt);
    }
  }

  throw lastError;
}

class CartBogoSameProductComponent extends Component {
  #isUpdating = false;
  /** @type {number[]} */
  #checkTimeouts = [];
  /** @type {Set<number>} */
  #eligibleVariantIds = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#refreshEligibleVariantIds();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    this.#scheduleCheck(150);
    this.#scheduleCheck(900);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refreshEligibleVariantIds();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#clearScheduledChecks();
  }

  /**
   * Cart section morphs and Shopify's cart JSON can briefly disagree right after
   * quantity changes. A short follow-up check keeps gift quantities in sync.
   * @param {number} delay
   */
  #scheduleCheck(delay) {
    const timeout = window.setTimeout(() => {
      this.#checkTimeouts = this.#checkTimeouts.filter((id) => id !== timeout);
      if (!this.isConnected) return;
      this.#checkBogoCondition();
    }, delay);

    this.#checkTimeouts.push(timeout);
  }

  #clearScheduledChecks() {
    for (const timeout of this.#checkTimeouts) {
      window.clearTimeout(timeout);
    }

    this.#checkTimeouts = [];
  }

  #refreshEligibleVariantIds() {
    this.#eligibleVariantIds = parseVariantIds(this.dataset.eligibleVariantIds);
  }

  #handleCartUpdate = (event) => {
    const source = event.detail?.data?.source || event.detail?.source;
    if (source === PROMO_SOURCE) return;

    this.#scheduleCheck(0);
    this.#scheduleCheck(600);
    this.#scheduleCheck(1500);
  };

  #refreshCart() {
    const target = this.closest('cart-drawer-component') || document.querySelector('cart-drawer-component') || document;

    target.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: { data: { source: PROMO_SOURCE } },
      })
    );
  }

  async #checkBogoCondition() {
    if (this.#isUpdating) return;

    try {
      this.#refreshEligibleVariantIds();

      const response = await fetchWithRetry('/cart.js');
      if (!response.ok) return;

      const cart = await response.json();
      const action = this.#getNextAction(cart);

      if (!action) return;

      if (action.type === 'add') {
        await this.#addGift(action.variantId, action.quantity);
      } else if (action.type === 'update') {
        await this.#updateGiftQuantity(action.item, action.quantity);
      } else if (action.type === 'remove') {
        await this.#removeGift(action.item);
      }
    } catch (error) {
      console.error('[Same-product BOGO]', error);
    }
  }

  /**
   * @param {any} cart
   * @returns {{type: 'add', variantId: number, quantity: number} | {type: 'update', item: any, quantity: number} | {type: 'remove', item: any} | null}
   */
  #getNextAction(cart) {
    /** @type {Map<number, number>} */
    const paidQuantityByVariant = new Map();
    /** @type {Map<number, any[]>} */
    const giftItemsByVariant = new Map();

    for (const item of cart.items || []) {
      const variantId = parseInt(item.variant_id, 10);
      if (!Number.isFinite(variantId)) continue;

      if (item.properties?.[GIFT_PROPERTY] === 'true') {
        pushToMapArray(giftItemsByVariant, variantId, item);
        continue;
      }

      if (item.selling_plan_allocation) continue;
      if (!this.#eligibleVariantIds.has(variantId) || isAutoGiftItem(item)) continue;

      paidQuantityByVariant.set(variantId, (paidQuantityByVariant.get(variantId) || 0) + item.quantity);
    }

    for (const [variantId, giftItems] of giftItemsByVariant.entries()) {
      const entitledQuantity = paidQuantityByVariant.get(variantId) || 0;
      const [primaryGift, ...extraGifts] = giftItems;

      if (entitledQuantity === 0) {
        return { type: 'remove', item: primaryGift };
      }

      if (extraGifts.length > 0) {
        return { type: 'remove', item: extraGifts[0] };
      }

      if (primaryGift.quantity !== entitledQuantity) {
        return { type: 'update', item: primaryGift, quantity: entitledQuantity };
      }
    }

    for (const [variantId, paidQuantity] of paidQuantityByVariant.entries()) {
      const giftQuantity = (giftItemsByVariant.get(variantId) || []).reduce((sum, item) => sum + item.quantity, 0);

      if (paidQuantity > 0 && giftQuantity === 0) {
        return { type: 'add', variantId, quantity: paidQuantity };
      }
    }

    return null;
  }

  /**
   * @param {number} variantId
   * @param {number} quantity
   */
  async #addGift(variantId, quantity) {
    if (this.#isUpdating || getGiftLock() || quantity <= 0) return;

    this.#isUpdating = true;
    setGiftLock(true);

    try {
      const formData = new FormData();
      formData.append('id', variantId.toString());
      formData.append('quantity', quantity.toString());
      formData.append(`properties[${GIFT_PROPERTY}]`, 'true');
      formData.append('properties[_free_gift]', 'true');
      formData.append(`properties[${SOURCE_VARIANT_PROPERTY}]`, variantId.toString());
      if (this.dataset.sectionId) formData.append('sections', this.dataset.sectionId);

      const response = await fetchWithRetry('/cart/add.js', { method: 'POST', body: formData });
      if (!response.ok) {
        let message = `Add failed: ${response.status}`;
        try {
          const data = await response.json();
          message = data.description || data.message || message;
        } catch {
          // The response was not JSON.
        }
        throw new Error(message);
      }

      this.#refreshCart();
    } finally {
      this.#isUpdating = false;
      setGiftLock(false);
    }
  }

  /**
   * @param {any} item
   * @param {number} quantity
   */
  async #updateGiftQuantity(item, quantity) {
    if (this.#isUpdating || quantity <= 0) return;

    this.#isUpdating = true;

    try {
      const response = await fetchWithRetry('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity,
          sections: this.dataset.sectionId || undefined,
        }),
      });

      if (response.ok) this.#refreshCart();
    } finally {
      this.#isUpdating = false;
    }
  }

  /**
   * @param {any} item
   */
  async #removeGift(item) {
    if (this.#isUpdating) return;

    this.#isUpdating = true;

    try {
      const response = await fetchWithRetry('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: 0,
          sections: this.dataset.sectionId || undefined,
        }),
      });

      if (response.ok) this.#refreshCart();
    } finally {
      this.#isUpdating = false;
    }
  }
}

if (!customElements.get('cart-bogo-same-product')) {
  customElements.define('cart-bogo-same-product', CartBogoSameProductComponent);
}
